import { useState, useCallback, useRef, useEffect, Fragment } from "react";
import { PdfViewer } from "@/components/PdfViewer";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, useAnimation } from "framer-motion";
import { authFetch } from "@/lib/authFetch";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertTriangle,
  Upload,
  UserPlus,
  Search,
  Loader2,
  RefreshCw,
  Check,
  CheckCheck,
  X,
  FileText,
  Download,
  Plus,
  ExternalLink,
  ThumbsUp,
  ThumbsDown,
  Minus,
  MapPin,
  Navigation,
  LayoutList,
  LayoutGrid,
  CreditCard,
  Undo2,
  Maximize2,
  ArrowRight,
  ArrowLeft,
  ArrowUp,
  User,
  Settings,
} from "lucide-react";
import { RhParametresSheet } from "@/components/rh/RhParametresSheet";
import type { Candidat, CandidatPiece } from "@shared/schema";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const emptyToNull = (v: unknown) =>
  v === "" || v === undefined ? null : v;

const normEmail = (v?: string | null) => (v || "").trim().toLowerCase() || null;
const normTel = (v?: string | null) => (v || "").replace(/[^\d+]/g, "") || null;

const getStoredView = (): VueType => {
  try { return (localStorage.getItem("rh_vue") as VueType) || "liste"; } catch { return "liste"; }
};
const setStoredView = (v: VueType) => {
  try { localStorage.setItem("rh_vue", v); } catch {}
};

// ─── Constantes ───────────────────────────────────────────────────────────────

const FILIERES = [
  { value: "technique", label: "Technique" },
  { value: "commercial", label: "Commercial" },
  { value: "administratif", label: "Administratif" },
];

const STATUTS = [
  { value: "nouveau", label: "Nouveau" },
  { value: "preselection", label: "Présélection" },
  { value: "entretien", label: "Entretien" },
  { value: "decision", label: "Décision finale" },
  { value: "archive", label: "Archivé" },
];

const SOURCES = [
  { value: "indeed", label: "Indeed" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "ecole", label: "École" },
  { value: "spontanee", label: "Spontanée" },
  { value: "autre", label: "Autre" },
];

const statutColor: Record<string, string> = {
  nouveau:      "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  preselection: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  entretien:    "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
  decision:     "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
  archive:      "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
};

const decisionLabel: Record<string, string> = {
  oui: "Oui", non: "Non", peut_etre: "Peut-être",
};

const decisionColor: Record<string, string> = {
  oui:       "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  non:       "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
  peut_etre: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
};

// ─── Types ────────────────────────────────────────────────────────────────────

type VueType = "deck" | "liste" | "cartes";

interface CandidatEnrichi extends Candidat {
  campagne_intitule?: string | null;
  lieu_ville?: string | null;
  pieces?: Array<{ id: string; type: string; nom_fichier: string | null; storage_path: string }>;
}

interface CandidatAvecPieces extends Candidat {
  pieces?: CandidatPiece[];
  doublons?: DoublonMatch[];
}

interface CampagneOption {
  id: string;
  intitule: string;
  filiere: string | null;
  statut: string;
  lieu_ville: string | null;
}

interface DoublonMatch {
  candidat: Candidat;
  matchType: "certain" | "probable";
  surEmail: boolean;
  surTel: boolean;
}

interface Filtres {
  q: string;
  filiere: string;
  statut: string;
  decision: string;
  campagne_id: string;
  lieu_id: string;
  niveau: string;
  hors_zone: string;
  tri: string;
}

interface UndoEntry {
  id: string;
  decision: string | null;
  statutPipeline: string;
  archivedAt: string | null;
}

// ─── Hook — useDecision ───────────────────────────────────────────────────────

function useDecision() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const undoStack = useRef<UndoEntry[]>([]);

  const decisionMutation = useMutation({
    mutationFn: async ({ id, decision }: { id: string; decision: string }) => {
      const r = await authFetch(`/api/candidats/${id}/decision`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ decision }),
      });
      if (!r.ok) throw new Error((await r.json()).message || "Erreur");
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/candidats"] }),
    onError: (e: Error) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const patchMutation = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Record<string, unknown> }) => {
      const r = await authFetch(`/api/candidats/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!r.ok) throw new Error((await r.json()).message || "Erreur");
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/candidats"] }),
    onError: (e: Error) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const decider = useCallback(async (
    candidat: CandidatEnrichi,
    d: "oui" | "non" | "peut_etre",
  ) => {
    if (undoStack.current.length >= 10) undoStack.current.shift();
    undoStack.current.push({
      id: candidat.id,
      decision: candidat.decision,
      statutPipeline: candidat.statutPipeline,
      archivedAt: candidat.archivedAt ? String(candidat.archivedAt) : null,
    });
    await decisionMutation.mutateAsync({ id: candidat.id, decision: d });
  }, [decisionMutation]);

  const annuler = useCallback(async (onRestored: () => void) => {
    const last = undoStack.current.pop();
    if (!last) return;
    await patchMutation.mutateAsync({
      id: last.id,
      patch: {
        decision: last.decision,
        statutPipeline: last.statutPipeline,
        archivedAt: last.archivedAt,
      },
    });
    onRestored();
  }, [patchMutation]);

  return {
    decider,
    annuler,
    canUndo: undoStack.current.length > 0,
    isPending: decisionMutation.isPending || patchMutation.isPending,
  };
}

// ─── Badges partagés ─────────────────────────────────────────────────────────

function StatutBadge({ statut }: { statut: string }) {
  return (
    <Badge className={`text-xs ${statutColor[statut] || "bg-gray-100 text-gray-600"}`}>
      {STATUTS.find((s) => s.value === statut)?.label || statut}
    </Badge>
  );
}

function DecisionBadge({ decision }: { decision: string | null }) {
  if (!decision) return <span className="text-muted-foreground text-xs">—</span>;
  return (
    <Badge className={`text-xs ${decisionColor[decision] || ""}`}>
      {decisionLabel[decision] || decision}
    </Badge>
  );
}

function NiveauBadge({ niveau }: { niveau: number | null }) {
  if (!niveau) return null;
  return (
    <Badge variant="outline" className="text-xs">
      Niv. {niveau}
    </Badge>
  );
}

function NoteEtoiles({
  value,
  readonly = false,
  onChange,
  size = "sm",
}: {
  value: number | null;
  readonly?: boolean;
  onChange?: (v: number | null) => void;
  size?: "xs" | "sm";
}) {
  const [hover, setHover] = useState<number | null>(null);
  const display = hover ?? value;
  const sz = size === "xs" ? "text-sm" : "text-lg";
  return (
    <div
      className={`flex gap-px items-center ${readonly ? "pointer-events-none" : "cursor-pointer"}`}
      data-testid="note-etoiles"
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <span
          key={n}
          className={`${sz} leading-none select-none transition-colors ${
            (display ?? 0) >= n
              ? "text-yellow-400"
              : "text-muted-foreground/25"
          }`}
          onClick={() => !readonly && onChange?.(value === n ? null : n)}
          onMouseEnter={() => !readonly && setHover(n)}
          onMouseLeave={() => !readonly && setHover(null)}
          data-testid={`star-${n}`}
        >
          ★
        </span>
      ))}
    </div>
  );
}

// ─── Boutons décision rapide ──────────────────────────────────────────────────

function BoutonsDecision({
  candidat,
  onDecision,
  isPending,
  size = "sm",
}: {
  candidat: CandidatEnrichi;
  onDecision: (c: CandidatEnrichi, d: "oui" | "non" | "peut_etre") => void;
  isPending: boolean;
  size?: "sm" | "xs";
}) {
  const cls = size === "xs" ? "h-6 px-2 text-xs" : "h-7 px-2 text-xs";
  return (
    <div className="flex gap-1">
      <Button size="sm" variant="outline" disabled={isPending}
        className={`${cls} border-red-200 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-400`}
        onClick={(e) => { e.stopPropagation(); onDecision(candidat, "non"); }}
        data-testid={`btn-non-${candidat.id}`}
      >
        <ThumbsDown className="w-3 h-3" />
      </Button>
      <Button size="sm" variant="outline" disabled={isPending}
        className={`${cls} border-amber-200 text-amber-700 hover:bg-amber-50 dark:border-amber-800 dark:text-amber-400`}
        onClick={(e) => { e.stopPropagation(); onDecision(candidat, "peut_etre"); }}
        data-testid={`btn-peutetre-${candidat.id}`}
      >
        <Minus className="w-3 h-3" />
      </Button>
      <Button size="sm" variant="outline" disabled={isPending}
        className={`${cls} border-green-200 text-green-700 hover:bg-green-50 dark:border-green-800 dark:text-green-400`}
        onClick={(e) => { e.stopPropagation(); onDecision(candidat, "oui"); }}
        data-testid={`btn-oui-${candidat.id}`}
      >
        <ThumbsUp className="w-3 h-3" />
      </Button>
    </div>
  );
}

// ─── Composant IA Proposition ─────────────────────────────────────────────────

function IAPropositionBlock({
  candidatId,
  proposition,
  onApply,
}: {
  candidatId: string;
  proposition: Record<string, unknown>;
  onApply: (patch: Record<string, unknown>) => void;
}) {
  const CHAMPS: { key: string; label: string }[] = [
    { key: "nom", label: "Nom" },
    { key: "prenom", label: "Prénom" },
    { key: "email", label: "Email" },
    { key: "telephone", label: "Téléphone" },
    { key: "ville", label: "Ville" },
    { key: "code_postal", label: "Code postal" },
    { key: "formation", label: "Formation" },
    { key: "experience_annees", label: "Expérience (années)" },
  ];

  const dbKey: Record<string, string> = {
    nom: "nom", prenom: "prenom", email: "email", telephone: "telephone",
    ville: "ville", code_postal: "codePostal", formation: "formation",
    experience_annees: "experienceAnnees",
  };

  const apply = (key: string, val: unknown) =>
    onApply({ [dbKey[key] || key]: emptyToNull(val) });

  const applyAll = () => {
    const patch: Record<string, unknown> = {};
    CHAMPS.forEach(({ key }) => {
      if (proposition[key] !== undefined && proposition[key] !== null)
        patch[dbKey[key] || key] = emptyToNull(proposition[key]);
    });
    onApply(patch);
  };

  const visible = CHAMPS.filter((c) => proposition[c.key] !== null && proposition[c.key] !== undefined);
  if (visible.length === 0) return null;

  return (
    <div className="border border-blue-200 dark:border-blue-800 rounded-lg p-3 bg-blue-50 dark:bg-blue-950/30 space-y-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wide">
          Proposition IA — à valider
        </span>
        <Button size="sm" variant="outline" className="h-6 text-xs" onClick={applyAll}>
          <CheckCheck className="w-3 h-3 mr-1" /> Tout appliquer
        </Button>
      </div>
      {visible.map(({ key, label }) => (
        <div key={key} className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground w-28 shrink-0">{label} :</span>
          <span className="flex-1 font-medium text-foreground truncate">{String(proposition[key])}</span>
          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-blue-700 dark:text-blue-300"
            onClick={() => apply(key, proposition[key])}>
            <Check className="w-3 h-3" />
          </Button>
        </div>
      ))}
    </div>
  );
}

// ─── Fiche candidat (Drawer) — 1A, inchangé ───────────────────────────────────

function CandidatDrawer({
  candidatId,
  onClose,
  onNavigate,
}: {
  candidatId: string | null;
  onClose: () => void;
  onNavigate: (id: string) => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();

  const { data: c, isLoading } = useQuery<CandidatAvecPieces>({
    queryKey: ["/api/candidats", candidatId],
    queryFn: async () => (await authFetch(`/api/candidats/${candidatId}`)).json(),
    enabled: !!candidatId,
    refetchInterval: (data) => (data as any)?.iaStatut === "en_cours" ? 3000 : false,
  });

  const { data: campagnesOuvertes = [] } = useQuery<CampagneOption[]>({
    queryKey: ["/api/campagnes"],
    queryFn: async () => (await authFetch("/api/campagnes")).json(),
    enabled: !!candidatId,
    select: (rows: any[]) => rows.filter((r) => r.statut === "ouverte"),
  });

  const patchMutation = useMutation({
    mutationFn: async (patch: Record<string, unknown>) => {
      const r = await authFetch(`/api/candidats/${candidatId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!r.ok) throw new Error((await r.json()).message || "Erreur");
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/candidats", candidatId] });
      qc.invalidateQueries({ queryKey: ["/api/candidats"] });
    },
    onError: (e: Error) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const decisionMutation = useMutation({
    mutationFn: async (decision: string) => {
      const r = await authFetch(`/api/candidats/${candidatId}/decision`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ decision }),
      });
      if (!r.ok) throw new Error((await r.json()).message || "Erreur");
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/candidats", candidatId] });
      qc.invalidateQueries({ queryKey: ["/api/candidats"] });
      toast({ title: "Décision enregistrée" });
    },
    onError: (e: Error) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const relancerIaMutation = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`/api/candidats/${candidatId}/relancer-ia`, { method: "POST" });
      if (!r.ok) throw new Error((await r.json()).message || "Erreur");
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/candidats", candidatId] }),
    onError: (e: Error) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const [ignorerHorsZone, setIgnorerHorsZone] = useState(false);
  const [pieceUploadType, setPieceUploadType] = useState<string>("");
  const pieceInputRef = useRef<HTMLInputElement>(null);

  const uploadPieceMutation = useMutation({
    mutationFn: async ({ file, type }: { file: File; type: string }) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("type", type);
      const r = await authFetch(`/api/candidats/${candidatId}/pieces`, { method: "POST", body: fd });
      if (!r.ok) throw new Error((await r.json()).message || "Erreur upload");
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/candidats", candidatId] });
      toast({ title: "Pièce ajoutée" });
    },
    onError: (e: Error) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const signedUrlMutation = useMutation({
    mutationFn: async (pieceId: string) => {
      const r = await authFetch(`/api/candidats/${candidatId}/pieces/${pieceId}/url`);
      if (!r.ok) throw new Error("Erreur URL");
      const { url } = await r.json();
      window.open(url, "_blank");
    },
    onError: (e: Error) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const [form, setForm] = useState<Record<string, unknown>>({});

  // Réinitialise tout l'état local quand on change de candidat
  useEffect(() => {
    setForm({});
    setIgnorerHorsZone(false);
    setPieceUploadType("");
  }, [candidatId]);

  const field = (key: keyof Candidat) => {
    const val = form[key] !== undefined ? form[key] : (c as any)?.[key];
    return val ?? "";
  };
  const setField = (key: string, val: unknown) => setForm((p) => ({ ...p, [key]: val }));
  const saveField = (key: string) => {
    const val = form[key];
    if (val === undefined) return;
    patchMutation.mutate({ [key]: emptyToNull(val) });
  };

  const applyIA = (patch: Record<string, unknown>) => {
    patchMutation.mutate(patch);
    setForm((p) => ({ ...p, ...patch }));
  };

  if (!candidatId) return null;

  return (
    <Sheet open={!!candidatId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="candidat-drawer">
        {isLoading || !c ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div key={c.id} className="space-y-5 py-2">
            <SheetHeader>
              <SheetTitle className="text-lg">{c.prenom} {c.nom || "Candidat sans nom"}</SheetTitle>
              <div className="flex flex-wrap gap-2 mt-1">
                {c.filiere && <Badge variant="outline" className="capitalize">{c.filiere}</Badge>}
                <StatutBadge statut={c.statutPipeline} />
                {c.decision && <DecisionBadge decision={c.decision} />}
              </div>
            </SheetHeader>

            {/* Bandeau doublons */}
            {Array.isArray(c.doublons) && c.doublons.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 space-y-2">
                <div className="flex items-center gap-2 text-amber-800 dark:text-amber-300 font-medium text-sm">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  Candidat déjà présent ({c.doublons[0].matchType === "certain" ? "correspondance certaine" : "correspondance probable"})
                </div>
                {c.doublons.map((d) => (
                  <div key={d.candidat.id} className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-2">
                    <span>{d.candidat.prenom} {d.candidat.nom}</span>
                    {d.surEmail && <Badge variant="outline" className="text-xs">email</Badge>}
                    {d.surTel && <Badge variant="outline" className="text-xs">tél</Badge>}
                    <Button size="sm" variant="link" className="h-auto p-0 text-xs" onClick={() => onNavigate(d.candidat.id)}>
                      Ouvrir la fiche <ExternalLink className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Bandeau hors zone suggéré */}
            {c.horsZoneSuggere && !c.horsZone && !ignorerHorsZone && (
              <div className="rounded-md border border-orange-300 bg-orange-50 dark:bg-orange-950/30 p-3 space-y-2">
                <div className="flex items-start gap-2 text-orange-800 dark:text-orange-300 text-sm">
                  <Navigation className="w-4 h-4 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <span className="font-medium">Probablement hors zone</span>
                    {c.distanceKm != null && (
                      <span className="ml-1 text-orange-700 dark:text-orange-400">— ≈ {c.distanceKm} km du bureau</span>
                    )}
                    <p className="text-xs text-orange-600 dark:text-orange-500 mt-0.5 italic">Distance à vol d'oiseau — estimation indicative.</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline"
                    className="h-7 text-xs border-orange-300 text-orange-800 hover:bg-orange-100 dark:text-orange-300 dark:border-orange-700"
                    onClick={() => patchMutation.mutate({ horsZone: true })}
                    disabled={patchMutation.isPending}
                    data-testid="btn-confirmer-hors-zone"
                  >
                    <MapPin className="w-3 h-3 mr-1" /> Confirmer hors zone
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground"
                    onClick={() => setIgnorerHorsZone(true)} data-testid="btn-ignorer-hors-zone">
                    Ignorer
                  </Button>
                </div>
              </div>
            )}

            {/* Statut IA */}
            {c.iaStatut === "en_cours" && (
              <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/30 rounded-md px-3 py-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Analyse du CV en cours…
              </div>
            )}
            {c.iaStatut === "echec" && (
              <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 rounded-md px-3 py-2">
                <AlertTriangle className="w-4 h-4" />
                Analyse IA échouée — complétez à la main.
                <Button size="sm" variant="ghost" className="h-6 text-xs ml-auto" onClick={() => relancerIaMutation.mutate()}>
                  <RefreshCw className="w-3 h-3 mr-1" /> Relancer
                </Button>
              </div>
            )}

            {/* Proposition IA */}
            {c.iaStatut === "fait" && c.iaProposition && (
              <IAPropositionBlock
                candidatId={c.id}
                proposition={c.iaProposition as Record<string, unknown>}
                onApply={applyIA}
              />
            )}

            {/* Campagne */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase text-muted-foreground">Campagne</Label>
              <Select
                value={(field("campagneId") as string) || "none"}
                onValueChange={(v) => {
                  const val = v === "none" ? null : v;
                  setField("campagneId", val);
                  patchMutation.mutate({ campagneId: val });
                }}
              >
                <SelectTrigger className="h-8 text-sm" data-testid="select-campagne-candidat">
                  <SelectValue placeholder="— Aucune campagne —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Aucune campagne —</SelectItem>
                  {campagnesOuvertes.map((camp) => (
                    <SelectItem key={camp.id} value={camp.id}>
                      {camp.intitule}{camp.lieu_ville ? ` · ${camp.lieu_ville}` : ""}
                    </SelectItem>
                  ))}
                  {c.campagneId && !campagnesOuvertes.find((camp) => camp.id === c.campagneId) && (
                    <SelectItem value={c.campagneId}>(Campagne clôturée)</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {c.distanceKm != null && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> Distance bureau : {c.distanceKm} km
                  {c.horsZone && <Badge className="ml-1 text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">Hors zone</Badge>}
                </p>
              )}
            </div>

            {/* Décision */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase text-muted-foreground">Décision</Label>
              <div className="flex gap-2">
                <Button size="sm" variant={c.decision === "oui" ? "default" : "outline"} className="flex-1 gap-1"
                  disabled={decisionMutation.isPending} onClick={() => decisionMutation.mutate("oui")}
                  data-testid="btn-decision-oui">
                  <ThumbsUp className="w-4 h-4" /> Oui
                </Button>
                <Button size="sm" variant={c.decision === "peut_etre" ? "secondary" : "outline"} className="flex-1 gap-1"
                  disabled={decisionMutation.isPending} onClick={() => decisionMutation.mutate("peut_etre")}
                  data-testid="btn-decision-peut-etre">
                  <Minus className="w-4 h-4" /> Peut-être
                </Button>
                <Button size="sm" variant={c.decision === "non" ? "destructive" : "outline"} className="flex-1 gap-1"
                  disabled={decisionMutation.isPending} onClick={() => decisionMutation.mutate("non")}
                  data-testid="btn-decision-non">
                  <ThumbsDown className="w-4 h-4" /> Non
                </Button>
              </div>
              {c.decision === "non" && (
                <p className="text-xs text-muted-foreground italic">Réponse de refus à envoyer au candidat — disponible en Phase 3.</p>
              )}
            </div>

            {/* Identité */}
            <section className="space-y-3">
              <Label className="text-xs font-semibold uppercase text-muted-foreground">Identité</Label>
              <div className="grid grid-cols-2 gap-3">
                {([ ["nom","Nom"],["prenom","Prénom"],["email","Email"],["telephone","Téléphone"],["ville","Ville"],["codePostal","Code postal"] ] as [keyof Candidat, string][]).map(([key, lbl]) => (
                  <div key={key}>
                    <Label className="text-xs text-muted-foreground">{lbl}</Label>
                    <Input value={String(field(key))} onChange={(e) => setField(key, e.target.value)}
                      onBlur={() => saveField(key)} className="h-8 text-sm" data-testid={`input-${key}`} />
                  </div>
                ))}
                <div>
                  <Label className="text-xs text-muted-foreground">Civilité (optionnel)</Label>
                  <Select
                    value={(field("civilite") as string) || "non_precise"}
                    onValueChange={(v) => {
                      const val = v === "non_precise" ? null : v;
                      setField("civilite", val);
                      patchMutation.mutate({ civilite: val });
                    }}
                  >
                    <SelectTrigger className="h-8 text-sm" data-testid="select-civilite">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="non_precise">—</SelectItem>
                      <SelectItem value="M.">M.</SelectItem>
                      <SelectItem value="Mme">Mme</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Permis B</Label>
                  <Select value={field("permisB") === true ? "oui" : field("permisB") === false ? "non" : "none"}
                    onValueChange={(v) => { const val = v === "oui" ? true : v === "non" ? false : null; setField("permisB", val); patchMutation.mutate({ permisB: val }); }}>
                    <SelectTrigger className="h-8 text-sm" data-testid="select-permisB">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      <SelectItem value="oui">Oui</SelectItem>
                      <SelectItem value="non">Non</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </section>

            {/* Évaluation */}
            <section className="space-y-3">
              <Label className="text-xs font-semibold uppercase text-muted-foreground">Évaluation</Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Filière</Label>
                  <Select value={(field("filiere") as string) || "none"}
                    onValueChange={(v) => { const val = v === "none" ? null : v; setField("filiere", val); patchMutation.mutate({ filiere: val }); }}>
                    <SelectTrigger className="h-8 text-sm" data-testid="select-filiere">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {FILIERES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Statut pipeline</Label>
                  <Select value={(field("statutPipeline") as string) || "nouveau"}
                    onValueChange={(v) => { setField("statutPipeline", v); patchMutation.mutate({ statutPipeline: v }); }}>
                    <SelectTrigger className="h-8 text-sm" data-testid="select-statut">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Niveau (1-2)</Label>
                  <Input type="number" min={1} max={2} value={String(field("niveau") || "")}
                    onChange={(e) => setField("niveau", emptyToNull(e.target.value))}
                    onBlur={() => saveField("niveau")} className="h-8 text-sm" data-testid="input-niveau" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Éval. CV</Label>
                  <div className="pt-1" data-testid="eval-cv-stars">
                    <NoteEtoiles
                      value={(field("evaluationCv") as number | null) || null}
                      onChange={(v) => {
                        setField("evaluationCv", v);
                        patchMutation.mutate({ evaluationCv: v });
                      }}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Contrat recherché</Label>
                  <Input value={String(field("contratRecherche") || "")}
                    onChange={(e) => setField("contratRecherche", e.target.value)}
                    onBlur={() => saveField("contratRecherche")} className="h-8 text-sm" data-testid="input-contratRecherche" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Rythme</Label>
                  <Input value={String(field("rythme") || "")}
                    onChange={(e) => setField("rythme", e.target.value)}
                    onBlur={() => saveField("rythme")} className="h-8 text-sm" data-testid="input-rythme" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Prétention brute</Label>
                  <Input value={String(field("pretentionBrut") || "")}
                    onChange={(e) => setField("pretentionBrut", e.target.value)}
                    onBlur={() => saveField("pretentionBrut")} className="h-8 text-sm" data-testid="input-pretentionBrut" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Formation</Label>
                  <Input value={String(field("formation") || "")}
                    onChange={(e) => setField("formation", e.target.value)}
                    onBlur={() => saveField("formation")} className="h-8 text-sm" data-testid="input-formation" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Avis</Label>
                <Textarea value={String(field("avis") || "")}
                  onChange={(e) => setField("avis", e.target.value)}
                  onBlur={() => saveField("avis")} className="text-sm min-h-16" data-testid="textarea-avis" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Résumé entretien</Label>
                <Textarea value={String(field("resumeEntretien") || "")}
                  onChange={(e) => setField("resumeEntretien", e.target.value)}
                  onBlur={() => saveField("resumeEntretien")} className="text-sm min-h-16" data-testid="textarea-resumeEntretien" />
              </div>
            </section>

            {/* Aperçu CV inline */}
            {(() => {
              const cvPiece = (c.pieces || []).find((p) => p.type === "cv");
              if (!cvPiece) return null;
              const isDocx = /\.docx?$/i.test(cvPiece.nomFichier || "");
              const adapte = {
                id: cvPiece.id,
                type: cvPiece.type,
                nom_fichier: cvPiece.nomFichier ?? null,
                storage_path: (cvPiece as any).storagePath ?? "",
              };
              return (
                <section className="space-y-2">
                  <Label className="text-xs font-semibold uppercase text-muted-foreground">Aperçu CV</Label>
                  {isDocx ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground border rounded-md p-3 bg-muted/30">
                      <FileText className="w-4 h-4 shrink-0" />
                      <span className="flex-1">Aperçu indisponible pour ce format (.docx)</span>
                      <Button size="sm" variant="outline" className="h-6 text-xs shrink-0"
                        onClick={() => signedUrlMutation.mutate(cvPiece.id)}
                        data-testid="btn-download-cv-docx">
                        Télécharger
                      </Button>
                    </div>
                  ) : (
                    <div className="h-80 rounded-lg overflow-hidden border bg-muted/30">
                      <CvApercu candidatId={c.id} piece={adapte} />
                    </div>
                  )}
                </section>
              );
            })()}

            {/* Pièces */}
            <section className="space-y-2">
              <Label className="text-xs font-semibold uppercase text-muted-foreground">Pièces jointes</Label>
              {(c.pieces || []).length === 0 ? (
                <p className="text-sm text-muted-foreground italic">Aucune pièce</p>
              ) : (
                <div className="space-y-1">
                  {(c.pieces || []).map((p) => (
                    <div key={p.id} className="flex items-center gap-2 text-sm border rounded-md px-3 py-1.5">
                      <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="flex-1 capitalize">{p.type.replace(/_/g, " ")}</span>
                      <span className="text-muted-foreground text-xs truncate max-w-32">{p.nomFichier}</span>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0"
                        onClick={() => signedUrlMutation.mutate(p.id)} data-testid={`btn-download-piece-${p.id}`}>
                        <Download className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <Select value={pieceUploadType} onValueChange={setPieceUploadType}>
                  <SelectTrigger className="h-8 text-sm flex-1" data-testid="select-piece-type">
                    <SelectValue placeholder="Type de pièce" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lettre_motivation">Lettre de motivation</SelectItem>
                    <SelectItem value="planning">Planning</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" disabled={!pieceUploadType || uploadPieceMutation.isPending}
                  onClick={() => pieceInputRef.current?.click()} data-testid="btn-upload-piece">
                  <Plus className="w-4 h-4 mr-1" /> Ajouter
                </Button>
                <input ref={pieceInputRef} type="file" className="hidden"
                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file && pieceUploadType) uploadPieceMutation.mutate({ file, type: pieceUploadType });
                    e.target.value = "";
                  }} />
              </div>
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ─── Dialog filière ────────────────────────────────────────────────────────────

function ChoixFiliereDialog({
  files,
  onConfirm,
  onCancel,
}: {
  files: File[];
  onConfirm: (filiere: string) => void;
  onCancel: () => void;
}) {
  const [filiere, setFiliere] = useState("");
  const preview = files.slice(0, 3);
  const extra = files.length - preview.length;
  return (
    <Dialog open={files.length > 0} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {files.length > 1 ? `Déposer ${files.length} CV` : "Choisir la filière"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="text-sm text-muted-foreground space-y-0.5">
            {preview.map((f, i) => (
              <p key={i}><span className="font-medium">{f.name}</span></p>
            ))}
            {extra > 0 && <p className="italic">… et {extra} autre(s)</p>}
          </div>
          <div>
            <Label className="text-sm mb-1 block">
              Filière (obligatoire{files.length > 1 ? " — appliquée à tous" : ""})
            </Label>
            <Select value={filiere} onValueChange={setFiliere}>
              <SelectTrigger data-testid="select-filiere-upload"><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent>
                {FILIERES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Annuler</Button>
          <Button
            disabled={!filiere}
            onClick={() => { const f = filiere; setFiliere(""); onConfirm(f); }}
            data-testid="btn-confirm-filiere"
          >
            Déposer {files.length > 1 ? `${files.length} CV` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Barre de filtres partagée ────────────────────────────────────────────────

function FiltresCandidats({
  value,
  onChange,
  campagnes,
  lieux,
}: {
  value: Filtres;
  onChange: (f: Partial<Filtres>) => void;
  campagnes: CampagneOption[];
  lieux: Array<{ id: string; ville: string; libelle: string }>;
}) {
  const hasFiltre = value.q || value.filiere !== "all" || value.statut !== "all" ||
    value.decision !== "all" || value.campagne_id !== "all" ||
    value.lieu_id !== "all" || value.niveau !== "all" || value.hors_zone !== "all";

  return (
    <div className="flex flex-wrap gap-2 items-center">
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
        <Input placeholder="Rechercher…" value={value.q}
          onChange={(e) => onChange({ q: e.target.value })}
          className="pl-8 h-8 w-44" data-testid="input-search-candidats" />
      </div>

      <Select value={value.filiere} onValueChange={(v) => onChange({ filiere: v })}>
        <SelectTrigger className="h-8 w-32" data-testid="select-filter-filiere"><SelectValue placeholder="Filière" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Toutes filières</SelectItem>
          {FILIERES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value={value.statut} onValueChange={(v) => onChange({ statut: v })}>
        <SelectTrigger className="h-8 w-32" data-testid="select-filter-statut"><SelectValue placeholder="Statut" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tous statuts</SelectItem>
          {STATUTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value={value.decision} onValueChange={(v) => onChange({ decision: v })}>
        <SelectTrigger className="h-8 w-32" data-testid="select-filter-decision"><SelectValue placeholder="Décision" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Toutes décisions</SelectItem>
          <SelectItem value="aucune">Aucune décision</SelectItem>
          <SelectItem value="oui">Oui</SelectItem>
          <SelectItem value="peut_etre">Peut-être</SelectItem>
          <SelectItem value="non">Non</SelectItem>
        </SelectContent>
      </Select>

      {campagnes.length > 0 && (
        <Select value={value.campagne_id} onValueChange={(v) => onChange({ campagne_id: v })}>
          <SelectTrigger className="h-8 w-36" data-testid="select-filter-campagne"><SelectValue placeholder="Campagne" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes campagnes</SelectItem>
            {campagnes.map((c) => <SelectItem key={c.id} value={c.id}>{c.intitule}</SelectItem>)}
          </SelectContent>
        </Select>
      )}

      {lieux.length > 0 && (
        <Select value={value.lieu_id} onValueChange={(v) => onChange({ lieu_id: v })}>
          <SelectTrigger className="h-8 w-32" data-testid="select-filter-lieu"><SelectValue placeholder="Bureau" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous bureaux</SelectItem>
            {lieux.map((l) => <SelectItem key={l.id} value={l.id}>{l.ville}</SelectItem>)}
          </SelectContent>
        </Select>
      )}

      <Select value={value.niveau} onValueChange={(v) => onChange({ niveau: v })}>
        <SelectTrigger className="h-8 w-28" data-testid="select-filter-niveau"><SelectValue placeholder="Niveau" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tous niveaux</SelectItem>
          <SelectItem value="1">Niveau 1</SelectItem>
          <SelectItem value="2">Niveau 2</SelectItem>
        </SelectContent>
      </Select>

      <Select value={value.hors_zone} onValueChange={(v) => onChange({ hors_zone: v })}>
        <SelectTrigger className="h-8 w-32" data-testid="select-filter-hors-zone"><SelectValue placeholder="Zone" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Toutes zones</SelectItem>
          <SelectItem value="false">En zone</SelectItem>
          <SelectItem value="true">Hors zone</SelectItem>
        </SelectContent>
      </Select>

      {hasFiltre && (
        <Button size="sm" variant="ghost" className="h-8"
          onClick={() => onChange({ q: "", filiere: "all", statut: "all", decision: "all", campagne_id: "all", lieu_id: "all", niveau: "all", hors_zone: "all" })}
          data-testid="btn-reset-filters">
          <X className="w-4 h-4 mr-1" /> Réinitialiser
        </Button>
      )}
    </div>
  );
}

// ─── Vue DECK ─────────────────────────────────────────────────────────────────

function CvApercu({
  candidatId,
  piece,
}: {
  candidatId: string;
  piece: { id: string; type: string; nom_fichier: string | null; storage_path: string } | undefined;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    if (!piece) return;
    setLoading(true);
    authFetch(`/api/candidats/${candidatId}/pieces/${piece.id}/url`)
      .then((r) => r.json())
      .then((d) => { setUrl(d.url || null); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [candidatId, piece?.id]);

  if (!piece) return (
    <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-2 bg-muted/30 rounded-lg">
      <FileText className="w-10 h-10 opacity-30" />
      <span className="text-xs">Aucun CV</span>
    </div>
  );

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
    </div>
  );

  const isImg = piece.nom_fichier?.match(/\.(png|jpe?g|gif|webp)$/i);
  const isPdf = !isImg;

  return (
    <Fragment>
      <div className="relative h-full w-full overflow-hidden rounded-lg">
        {isImg ? (
          <img src={url || ""} alt="CV" className="w-full h-full object-contain rounded-lg" />
        ) : (
          <PdfViewer url={url || ""} className="h-full w-full" />
        )}
        {url && (
          <Button size="sm" variant="secondary"
            className="absolute top-2 right-2 h-7 text-xs gap-1 opacity-80 hover:opacity-100 z-10"
            onClick={() => isPdf ? setFullscreen(true) : window.open(url, "_blank")}
            data-testid="btn-open-cv-fullscreen">
            <Maximize2 className="w-3 h-3" /> Ouvrir
          </Button>
        )}
      </div>

      {/* Modale plein écran — PDF uniquement */}
      <Dialog open={fullscreen} onOpenChange={setFullscreen}>
        <DialogContent className="max-w-4xl w-full h-[90vh] flex flex-col p-4 gap-3">
          <DialogHeader className="shrink-0">
            <DialogTitle className="text-sm font-medium truncate">
              {piece.nom_fichier ?? "CV"}
            </DialogTitle>
          </DialogHeader>
          {url && (
            <PdfViewer url={url} className="flex-1 min-h-0 rounded-md border bg-muted/20" />
          )}
        </DialogContent>
      </Dialog>
    </Fragment>
  );
}

function DeckView({
  candidats,
  onDecision,
  onUndo,
  onOpen,
  canUndo,
  isPending,
}: {
  candidats: CandidatEnrichi[];
  onDecision: (c: CandidatEnrichi, d: "oui" | "non" | "peut_etre") => Promise<void>;
  onUndo: (cb: () => void) => void;
  onOpen: (c: CandidatEnrichi) => void;
  canUndo: boolean;
  isPending: boolean;
}) {
  const [idx, setIdx] = useState(0);
  const controls = useAnimation();

  const courant = candidats[idx];
  const suivant = candidats[idx + 1];

  const decider = useCallback(async (d: "oui" | "non" | "peut_etre") => {
    if (!courant || isPending) return;
    const xOut = d === "oui" ? 400 : d === "non" ? -400 : 0;
    const yOut = d === "peut_etre" ? -400 : 0;
    await controls.start({ x: xOut, y: yOut, opacity: 0, transition: { duration: 0.25 } });
    await onDecision(courant, d);
    controls.set({ x: 0, y: 0, opacity: 1 });
    setIdx((n) => n + 1);
  }, [courant, isPending, controls, onDecision]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName?.toUpperCase();
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.key === "ArrowRight")  decider("oui");
      else if (e.key === "ArrowLeft")  decider("non");
      else if (e.key === "ArrowUp") { e.preventDefault(); decider("peut_etre"); }
      else if (e.key === "ArrowDown" && courant) { e.preventDefault(); onOpen(courant); }
      else if (e.key === "Backspace") { e.preventDefault(); onUndo(() => setIdx((n) => Math.max(0, n - 1))); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [courant, decider, onOpen, onUndo]);

  if (!courant) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4 text-muted-foreground" data-testid="deck-empty">
        <span className="text-4xl">🎉</span>
        <p className="text-base font-medium">Tout est trié — plus de candidats à traiter avec ces filtres.</p>
        {canUndo && (
          <Button variant="outline" size="sm" onClick={() => onUndo(() => setIdx((n) => Math.max(0, n - 1)))}>
            <Undo2 className="w-4 h-4 mr-1" /> Annuler la dernière décision
          </Button>
        )}
      </div>
    );
  }

  const ia = courant.iaProposition as Record<string, unknown> | null;
  const cvPiece = courant.pieces?.find((p) => p.type === "cv");

  return (
    <div className="flex flex-col items-center gap-4 py-4" data-testid="deck-view">
      {/* Pile de cartes */}
      <div className="relative w-full max-w-2xl" style={{ height: 480 }}>
        {/* Carte suivante (dessous) */}
        {suivant && (
          <div className="absolute inset-0 translate-y-3 scale-95 rounded-xl border bg-card opacity-60 pointer-events-none" />
        )}

        {/* Carte courante */}
        <motion.div
          key={courant.id}
          drag
          dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
          dragElastic={0.2}
          onDragEnd={(_, info) => {
            if (info.offset.x > 120) decider("oui");
            else if (info.offset.x < -120) decider("non");
            else if (info.offset.y < -120) decider("peut_etre");
          }}
          animate={controls}
          className="absolute inset-0 rounded-xl border bg-card shadow-lg overflow-hidden cursor-grab active:cursor-grabbing"
          data-testid={`deck-card-${courant.id}`}
        >
          <div className="flex h-full">
            {/* Aperçu CV */}
            <div className="w-1/2 p-3 border-r">
              <CvApercu candidatId={courant.id} piece={cvPiece} />
            </div>

            {/* Résumé */}
            <div className="w-1/2 p-4 flex flex-col gap-3 overflow-y-auto">
              <div>
                <h2 className="font-semibold text-base leading-tight">
                  {courant.prenom} {courant.nom || <span className="text-muted-foreground italic">Sans nom</span>}
                </h2>
                {courant.posteVise && <p className="text-sm text-muted-foreground">{courant.posteVise}</p>}
                {courant.campagne_intitule && (
                  <p className="text-xs text-muted-foreground mt-0.5">📋 {courant.campagne_intitule}</p>
                )}
              </div>

              {(courant.ville || courant.distanceKm != null || courant.horsZoneSuggere) && (
                <div className="space-y-1 text-xs text-muted-foreground">
                  {courant.ville && <p className="flex items-center gap-1"><MapPin className="w-3 h-3" />{courant.ville}{courant.codePostal ? ` (${courant.codePostal})` : ""}</p>}
                  {courant.distanceKm != null && <p>↔ {courant.distanceKm} km du bureau</p>}
                  {courant.horsZoneSuggere && !courant.horsZone && (
                    <Badge className="text-xs bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                      Probablement hors zone
                    </Badge>
                  )}
                </div>
              )}

              {ia?.formation && <p className="text-xs"><span className="text-muted-foreground">Formation :</span> {String(ia.formation)}</p>}
              {ia?.experience_annees != null && <p className="text-xs"><span className="text-muted-foreground">Expérience :</span> {String(ia.experience_annees)} ans</p>}

              {courant.iaStatut === "en_cours" && (
                <div className="flex items-center gap-1 text-xs text-blue-600">
                  <Loader2 className="w-3 h-3 animate-spin" /> Analyse IA en cours…
                </div>
              )}

              <div className="flex gap-1 flex-wrap mt-auto pt-1">
                <StatutBadge statut={courant.statutPipeline} />
                {courant.filiere && <Badge variant="outline" className="text-xs capitalize">{courant.filiere}</Badge>}
                <NiveauBadge niveau={courant.niveau} />
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Indicateur drag */}
      <p className="text-xs text-muted-foreground flex items-center gap-3">
        <span><ArrowLeft className="w-3 h-3 inline" /> Non</span>
        <span><ArrowUp className="w-3 h-3 inline" /> Peut-être</span>
        <span>Oui <ArrowRight className="w-3 h-3 inline" /></span>
      </p>

      {/* Actions boutons */}
      <div className="flex items-center gap-3">
        <Button size="sm" variant="outline"
          className="gap-1 border-red-200 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-400"
          disabled={isPending} onClick={() => decider("non")} data-testid="btn-deck-non">
          <ThumbsDown className="w-4 h-4" /> Non
        </Button>
        <Button size="sm" variant="outline"
          className="gap-1 border-amber-200 text-amber-700 hover:bg-amber-50 dark:border-amber-800 dark:text-amber-400"
          disabled={isPending} onClick={() => decider("peut_etre")} data-testid="btn-deck-peutetre">
          <Minus className="w-4 h-4" /> Peut-être
        </Button>
        <Button size="sm" variant="outline"
          className="gap-1 border-green-200 text-green-700 hover:bg-green-50 dark:border-green-800 dark:text-green-400"
          disabled={isPending} onClick={() => decider("oui")} data-testid="btn-deck-oui">
          <ThumbsUp className="w-4 h-4" /> Oui
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onOpen(courant)} data-testid="btn-deck-ouvrir">
          <ExternalLink className="w-4 h-4 mr-1" /> Fiche
        </Button>
        <Button size="sm" variant="ghost" disabled={!canUndo || isPending}
          onClick={() => onUndo(() => setIdx((n) => Math.max(0, n - 1)))}
          data-testid="btn-deck-annuler">
          <Undo2 className="w-4 h-4 mr-1" /> Annuler
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">{idx + 1} / {candidats.length}</p>
    </div>
  );
}

// ─── Vue LISTE ────────────────────────────────────────────────────────────────

function ListeView({
  candidats,
  onOpen,
  onDecision,
  isPending,
  tri,
  onTri,
}: {
  candidats: CandidatEnrichi[];
  onOpen: (c: CandidatEnrichi) => void;
  onDecision: (c: CandidatEnrichi, d: "oui" | "non" | "peut_etre") => void;
  isPending: boolean;
  tri: string;
  onTri: (t: string) => void;
}) {
  if (candidats.length === 0) return (
    <div className="text-center text-muted-foreground py-16 text-sm">Aucun candidat trouvé</div>
  );

  const sortHeader = (label: string, key: string) => (
    <TableHead className="cursor-pointer hover:text-foreground select-none"
      onClick={() => onTri(key)} data-testid={`sort-${key}`}>
      {label} {tri === key ? "↓" : ""}
    </TableHead>
  );

  return (
    <div className="border rounded-lg overflow-hidden" data-testid="liste-view">
      <Table>
        <TableHeader>
          <TableRow>
            {sortHeader("Nom / Prénom", "nom_asc")}
            <TableHead>Filière</TableHead>
            <TableHead>Campagne</TableHead>
            <TableHead>Bureau</TableHead>
            <TableHead>Statut</TableHead>
            <TableHead>Décision</TableHead>
            {sortHeader("Niv.", "evaluation_desc")}
            {sortHeader("Dist.", "distance_asc")}
            <TableHead>Ville</TableHead>
            {sortHeader("Date", "date_desc")}
            <TableHead>IA</TableHead>
            <TableHead className="w-24">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {candidats.map((c) => (
            <TableRow key={c.id} className="cursor-pointer group"
              onClick={() => onOpen(c)} data-testid={`row-candidat-${c.id}`}>
              <TableCell className="font-medium whitespace-nowrap">
                {c.prenom} {c.nom || <span className="text-muted-foreground italic text-xs">Sans nom</span>}
              </TableCell>
              <TableCell>
                {c.filiere ? <Badge variant="outline" className="capitalize text-xs">{c.filiere}</Badge>
                  : <span className="text-muted-foreground text-xs">—</span>}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground max-w-28 truncate">
                {c.campagne_intitule || "—"}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">{c.lieu_ville || "—"}</TableCell>
              <TableCell><StatutBadge statut={c.statutPipeline} /></TableCell>
              <TableCell><DecisionBadge decision={c.decision} /></TableCell>
              <TableCell className="text-center">
                {c.evaluationCv != null
                  ? <NoteEtoiles value={c.evaluationCv} readonly size="xs" />
                  : <span className="text-muted-foreground text-xs">—</span>}
              </TableCell>
              <TableCell className="text-xs text-center">
                {c.distanceKm != null ? `${c.distanceKm} km` : "—"}
                {c.horsZone && <Badge className="ml-1 text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">HZ</Badge>}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">{c.ville || "—"}</TableCell>
              <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                {c.datePostulation ? new Date(c.datePostulation).toLocaleDateString("fr-FR")
                  : c.createdAt ? new Date(c.createdAt).toLocaleDateString("fr-FR") : "—"}
              </TableCell>
              <TableCell>
                {c.iaStatut === "en_cours" && <Loader2 className="w-4 h-4 animate-spin text-blue-500" />}
                {c.iaStatut === "fait" && <Check className="w-4 h-4 text-green-500" />}
                {c.iaStatut === "echec" && <X className="w-4 h-4 text-red-500" />}
              </TableCell>
              <TableCell onClick={(e) => e.stopPropagation()}>
                <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                  <BoutonsDecision candidat={c} onDecision={onDecision} isPending={isPending} size="xs" />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ─── Vue CARTES ───────────────────────────────────────────────────────────────

function CartesView({
  candidats,
  onOpen,
  onDecision,
  isPending,
}: {
  candidats: CandidatEnrichi[];
  onOpen: (c: CandidatEnrichi) => void;
  onDecision: (c: CandidatEnrichi, d: "oui" | "non" | "peut_etre") => void;
  isPending: boolean;
}) {
  if (candidats.length === 0) return (
    <div className="text-center text-muted-foreground py-16 text-sm">Aucun candidat trouvé</div>
  );

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="cartes-view">
      {candidats.map((c) => (
        <div key={c.id}
          className="border rounded-xl bg-card p-4 flex flex-col gap-3 hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => onOpen(c)} data-testid={`carte-candidat-${c.id}`}>

          {/* Avatar + nom */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <span className="text-sm font-semibold text-primary">
                {(c.prenom?.[0] || c.nom?.[0] || "?").toUpperCase()}
              </span>
            </div>
            <div className="min-w-0">
              <p className="font-medium text-sm leading-tight truncate">
                {c.prenom} {c.nom || ""}
              </p>
              {c.posteVise && <p className="text-xs text-muted-foreground truncate">{c.posteVise}</p>}
            </div>
          </div>

          {/* Campagne */}
          {c.campagne_intitule && (
            <p className="text-xs text-muted-foreground truncate">📋 {c.campagne_intitule}</p>
          )}

          {/* Badges */}
          <div className="flex flex-wrap gap-1">
            {c.filiere && <Badge variant="outline" className="text-xs capitalize">{c.filiere}</Badge>}
            <StatutBadge statut={c.statutPipeline} />
            <NiveauBadge niveau={c.niveau} />
            {c.horsZone && <Badge className="text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">Hors zone</Badge>}
          </div>
          {c.evaluationCv != null && (
            <NoteEtoiles value={c.evaluationCv} readonly size="xs" />
          )}

          {/* Ville */}
          {c.ville && (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <MapPin className="w-3 h-3" />{c.ville}
              {c.distanceKm != null && ` · ${c.distanceKm} km`}
            </p>
          )}

          {/* Décision + actions */}
          <div className="flex items-center justify-between mt-auto pt-2 border-t" onClick={(e) => e.stopPropagation()}>
            <DecisionBadge decision={c.decision} />
            <BoutonsDecision candidat={c} onDecision={onDecision} isPending={isPending} size="xs" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────

const FILTRES_DEFAUT: Filtres = {
  q: "", filiere: "all", statut: "all", decision: "all",
  campagne_id: "all", lieu_id: "all", niveau: "all", hors_zone: "all", tri: "date_desc",
};

export default function CandidatsPage() {
  const { toast } = useToast();
  const qc = useQueryClient();

  const [vue, setVueRaw] = useState<VueType>(getStoredView);
  const setVue = (v: VueType) => { setVueRaw(v); setStoredView(v); };

  const [filtres, setFiltres] = useState<Filtres>(FILTRES_DEFAUT);
  const updateFiltres = (f: Partial<Filtres>) => setFiltres((p) => ({ ...p, ...f }));

  // Deck : scope par défaut statut=nouveau & decision=aucune si aucun filtre posé
  const filtresEffectifs: Filtres = vue === "deck" && filtres === FILTRES_DEFAUT
    ? { ...FILTRES_DEFAUT, statut: "nouveau", decision: "aucune" }
    : filtres;

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [rhParamsOpen, setRhParamsOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ─── Query candidats enrichis ──────────────────────────────────────────────
  const qParams = new URLSearchParams();
  if (filtresEffectifs.q) qParams.set("q", filtresEffectifs.q);
  if (filtresEffectifs.filiere !== "all") qParams.set("filiere", filtresEffectifs.filiere);
  if (filtresEffectifs.statut !== "all") qParams.set("statut", filtresEffectifs.statut);
  if (filtresEffectifs.decision !== "all") qParams.set("decision", filtresEffectifs.decision);
  if (filtresEffectifs.campagne_id !== "all") qParams.set("campagne_id", filtresEffectifs.campagne_id);
  if (filtresEffectifs.lieu_id !== "all") qParams.set("lieu_id", filtresEffectifs.lieu_id);
  if (filtresEffectifs.niveau !== "all") qParams.set("niveau", filtresEffectifs.niveau);
  if (filtresEffectifs.hors_zone !== "all") qParams.set("hors_zone", filtresEffectifs.hors_zone);
  qParams.set("tri", filtresEffectifs.tri);
  qParams.set("limit", "200");

  const { data: candidats = [], isLoading } = useQuery<CandidatEnrichi[]>({
    queryKey: ["/api/candidats", filtresEffectifs],
    queryFn: async () => (await authFetch(`/api/candidats?${qParams}`)).json(),
  });

  const { data: campagnes = [] } = useQuery<CampagneOption[]>({
    queryKey: ["/api/campagnes"],
    queryFn: async () => (await authFetch("/api/campagnes")).json(),
  });

  const { data: lieux = [] } = useQuery<Array<{ id: string; ville: string; libelle: string; actif: boolean }>>({
    queryKey: ["/api/lieux-recrutement"],
    queryFn: async () => (await authFetch("/api/lieux-recrutement")).json(),
  });

  // ─── Hook décision partagé ─────────────────────────────────────────────────
  const { decider, annuler, canUndo, isPending: decisionPending } = useDecision();

  // ─── Upload mutations ──────────────────────────────────────────────────────
  const uploadCvMutation = useMutation({
    mutationFn: async ({ file, filiere }: { file: File; filiere: string }) => {
      const fd = new FormData();
      fd.append("cv", file);
      fd.append("filiere", filiere);
      const r = await authFetch("/api/candidats/upload-cv", { method: "POST", body: fd });
      if (!r.ok) throw new Error((await r.json()).message || "Erreur upload");
      return r.json();
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["/api/candidats"] });
      setSelectedId(data.id);
      toast({ title: "CV déposé", description: "Analyse en cours…" });
    },
    onError: (e: Error) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const createManualMutation = useMutation({
    mutationFn: async () => {
      const r = await authFetch("/api/candidats", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filiere: null }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || "Erreur création");
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["/api/candidats"] });
      setSelectedId(data.id);
    },
    onError: (e: Error) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const handleFilesDrop = useCallback((files: File[]) => {
    if (files.length > 0) setPendingFiles(files);
  }, []);
  const onDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const onDragLeave = () => setIsDragging(false);
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault(); setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) handleFilesDrop(files);
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* En-tête */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Recrutement</h1>
          <p className="text-sm text-muted-foreground">{candidats.length} candidat(s)</p>
        </div>
        <div className="flex gap-2 items-center">
          {/* Switcher de vue */}
          <div className="flex rounded-md border overflow-hidden" data-testid="vue-switcher">
            {([
              { v: "deck" as VueType,   icon: CreditCard, label: "Deck" },
              { v: "liste" as VueType,  icon: LayoutList,  label: "Liste" },
              { v: "cartes" as VueType, icon: LayoutGrid,  label: "Cartes" },
            ] as const).map(({ v, icon: Icon, label }) => (
              <button key={v}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
                  vue === v
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted"
                }`}
                onClick={() => setVue(v)}
                data-testid={`btn-vue-${v}`}
              >
                <Icon className="w-3.5 h-3.5" />{label}
              </button>
            ))}
          </div>

          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="btn-deposer-cv">
            <Upload className="w-4 h-4 mr-1" /> Déposer des CV
          </Button>
          <input ref={fileInputRef} type="file" multiple className="hidden" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
            onChange={(e) => { const files = Array.from(e.target.files || []); if (files.length > 0) handleFilesDrop(files); e.target.value = ""; }} />
          <Button size="sm" onClick={() => createManualMutation.mutate()} disabled={createManualMutation.isPending} data-testid="btn-ajouter-manuellement">
            <UserPlus className="w-4 h-4 mr-1" /> Ajouter manuellement
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Paramètres RH" onClick={() => setRhParamsOpen(true)} data-testid="btn-rh-parametres">
            <Settings className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Zone drag & drop */}
      <div onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
        className={`border-2 border-dashed rounded-lg px-4 py-4 text-center text-sm transition-colors cursor-pointer ${
          isDragging ? "border-primary bg-primary/5 text-primary" : "border-muted-foreground/25 text-muted-foreground hover:border-primary/40"
        }`}
        onClick={() => fileInputRef.current?.click()} data-testid="dropzone-cv">
        <Upload className="w-5 h-5 mx-auto mb-1 opacity-50" />
        Glissez un ou plusieurs CV ici ou cliquez pour parcourir
      </div>
      {uploadProgress && (
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-1">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>{uploadProgress.done} / {uploadProgress.total} CV traités</span>
        </div>
      )}

      {/* Barre de filtres */}
      <FiltresCandidats
        value={filtres}
        onChange={updateFiltres}
        campagnes={campagnes}
        lieux={lieux.filter((l) => l.actif)}
      />

      {/* Tri (liste uniquement) */}
      {vue === "liste" && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Trier par :</span>
          <Select value={filtres.tri} onValueChange={(v) => updateFiltres({ tri: v })}>
            <SelectTrigger className="h-7 w-40 text-xs" data-testid="select-tri">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date_desc">Date (plus récent)</SelectItem>
              <SelectItem value="nom_asc">Nom A→Z</SelectItem>
              <SelectItem value="evaluation_desc">Éval. CV (↓)</SelectItem>
              <SelectItem value="distance_asc">Distance (↑)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Rendu selon la vue */}
      {isLoading ? (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : vue === "deck" ? (
        <DeckView
          candidats={candidats}
          onDecision={decider}
          onUndo={annuler}
          onOpen={(c) => setSelectedId(c.id)}
          canUndo={canUndo}
          isPending={decisionPending}
        />
      ) : vue === "liste" ? (
        <ListeView
          candidats={candidats}
          onOpen={(c) => setSelectedId(c.id)}
          onDecision={(c, d) => { decider(c, d); }}
          isPending={decisionPending}
          tri={filtres.tri}
          onTri={(t) => updateFiltres({ tri: t })}
        />
      ) : (
        <CartesView
          candidats={candidats}
          onOpen={(c) => setSelectedId(c.id)}
          onDecision={(c, d) => { decider(c, d); }}
          isPending={decisionPending}
        />
      )}

      {/* Dialog filière pour upload */}
      <ChoixFiliereDialog
        files={pendingFiles}
        onConfirm={async (filiere) => {
          const files = pendingFiles;
          setPendingFiles([]);
          setUploadProgress({ done: 0, total: files.length });
          for (const file of files) {
            try {
              await uploadCvMutation.mutateAsync({ file, filiere });
            } catch {}
            setUploadProgress((p) => p ? { ...p, done: p.done + 1 } : null);
          }
          setUploadProgress(null);
        }}
        onCancel={() => setPendingFiles([])}
      />

      {/* Drawer fiche candidat — key force le remontage complet au changement de candidat */}
      <CandidatDrawer
        key={selectedId ?? "none"}
        candidatId={selectedId}
        onClose={() => setSelectedId(null)}
        onNavigate={(id) => setSelectedId(id)}
      />

      <RhParametresSheet open={rhParamsOpen} onClose={() => setRhParamsOpen(false)} />
    </div>
  );
}
